import { Component, type ErrorInfo, type ReactNode } from 'react';
import { SUPPORT_URL } from '../utils/support';
import styles from './ErrorBoundary.module.css';

interface Props {
  children: ReactNode;
  /** Rendered in place of the children once a descendant throws. */
  fallback: ReactNode;
  /** Names the boundary in the console record, so a report says which one caught. */
  label: string;
}

interface State {
  failed: boolean;
}

/**
 * React unmounts the entire tree when a render throws, so without a boundary any
 * unexpected shape in a message, citation or session response blanks the page with
 * no message and no recovery but a manual reload.
 *
 * Has to be a class: `getDerivedStateFromError` and `componentDidCatch` have no hook
 * equivalent in React 18. Note neither fires for errors thrown outside rendering,
 * so async failures still need their own handling in the hooks.
 *
 * There is deliberately no reset. Clearing the error state only remounts the subtree,
 * and at both altitudes this is used the subtree owns everything worth keeping, so a
 * reset is a strictly weaker page reload that re-crashes on any deterministic bug.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console is the only destination today, which means a crash for a real user
    // is invisible to us. Client-side error reporting is tracked separately.
    console.error(`[error-boundary:${this.props.label}]`, error, info.componentStack);
  }

  render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

// Duplicated from SettingsMenu rather than shared through a constant, deliberately.
// This component renders when the app is already broken, so it takes no import that
// could be the thing that failed to evaluate. Keep the two in step by hand.

/**
 * Whole-app fallback. Deliberately says nothing about the error itself: the message
 * and stack go to the console, not to a customer's screen.
 */
export function AppErrorFallback() {
  return (
    <div className={styles.container} role="alert">
      <div className={styles.bloom} aria-hidden="true" />
      <div className={styles.content}>
        <h1 className={styles.title}>Oops, something went wrong</h1>
        <p className={styles.body}>
          Sorry for the inconvenience. Try reloading the page, and if the problem persists,{' '}
          <a className={styles.link} href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
            contact our support
          </a>
          .
        </p>
        <button type="button" className={styles.reload} onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    </div>
  );
}
